import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Layers, Check, Sparkles, Globe, Power, Star } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import DataTable from "@/components/app/DataTable";
import Modal from "@/components/app/Modal";
import { Field, CheckField, useValidatedForm, isBlank, toNum } from "@/components/app/FormKit";
import { btnOutline, btnPrimary, iconBtn, inr, pageSub, pageTitle } from "@/components/app/ui";

const UNLIMITED = 1000000;
const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const empty = {
  name: "", id: "", price: "", product_limit: "100", unlimited: false, description: "", features: "",
  ai_theme: false, custom_domain: false, popular: false, active: true, sort_order: "0",
};
const limitText = (n) => (n >= UNLIMITED ? "Unlimited" : Number(n).toLocaleString("en-IN"));
const toPayload = (p) => ({
  name: p.name, price: p.price, product_limit: p.product_limit, description: p.description || "", features: p.features || [],
  ai_theme: !!p.ai_theme, custom_domain: !!p.custom_domain, popular: !!p.popular, active: p.active !== false, sort_order: p.sort_order || 0,
});

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [idEdited, setIdEdited] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/admin/plans").then(({ data }) => setPlans(data)).finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);

  const isFree = editId === "free";

  const validate = useCallback((f) => {
    const e = {};
    const name = f.name.trim();
    if (!name) e.name = "Plan name is required.";
    else if (name.length < 2) e.name = "Name must be at least 2 characters.";
    else if (name.length > 40) e.name = "Name must be 40 characters or fewer.";
    else if (plans.some((p) => p.id !== editId && p.name.trim().toLowerCase() === name.toLowerCase())) e.name = "A plan with this name already exists.";
    if (!editId) {
      const id = f.id.trim();
      if (!id) e.id = "Plan ID is required.";
      else if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(id) || id.length < 2 || id.length > 30) e.id = "2–30 characters: lowercase letters, numbers and hyphens.";
      else if (plans.some((p) => p.id === id)) e.id = "This plan ID is already in use.";
    }
    const price = toNum(f.price);
    if (isBlank(f.price)) e.price = "Price is required (use 0 for a free plan).";
    else if (!(price >= 0)) e.price = "Price cannot be negative.";
    else if (price > 10000000) e.price = "Price is too large.";
    if (!f.unlimited) {
      const lim = toNum(f.product_limit);
      if (isBlank(f.product_limit)) e.product_limit = "Product limit is required.";
      else if (!Number.isInteger(lim) || lim < 1) e.product_limit = "Whole number, 1 or more.";
      else if (lim >= UNLIMITED) e.product_limit = "Use the Unlimited option instead.";
    }
    if (!isBlank(f.sort_order) && (!Number.isInteger(toNum(f.sort_order)) || toNum(f.sort_order) < 0)) e.sort_order = "Whole number, 0 or more.";
    const lines = f.features.split("\n").map((l) => l.trim()).filter(Boolean);
    if (lines.length > 12) e.features = "At most 12 features.";
    else if (lines.some((l) => l.length > 80)) e.features = "Each feature must be 80 characters or fewer.";
    if (f.description.length > 120) e.description = "Keep it under 120 characters.";
    return e;
  }, [plans, editId]);

  const form = useValidatedForm(empty, validate);
  const { values: v, bind, set } = form;

  const openNew = () => { form.reset({ ...empty, sort_order: String(plans.length) }); setEditId(null); setIdEdited(false); setOpen(true); };
  const openEdit = (p) => {
    form.reset({
      ...empty, name: p.name, id: p.id, price: String(p.price), product_limit: p.product_limit >= UNLIMITED ? "100" : String(p.product_limit),
      unlimited: p.product_limit >= UNLIMITED, description: p.description || "", features: (p.features || []).join("\n"),
      ai_theme: !!p.ai_theme, custom_domain: !!p.custom_domain, popular: !!p.popular, active: p.active !== false, sort_order: String(p.sort_order ?? 0),
    });
    setEditId(p.id); setIdEdited(true); setOpen(true);
  };

  const buildPayload = () => ({
    name: v.name.trim(), price: parseFloat(v.price), product_limit: v.unlimited ? UNLIMITED : parseInt(v.product_limit, 10),
    description: v.description.trim(), features: v.features.split("\n").map((l) => l.trim()).filter(Boolean),
    ai_theme: v.ai_theme, custom_domain: v.custom_domain, popular: v.popular, active: isFree ? true : v.active, sort_order: parseInt(v.sort_order, 10) || 0,
  });

  const save = async () => {
    if (!form.check()) return;
    setSaving(true);
    const payload = buildPayload();
    try {
      if (editId) { await api.put(`/admin/plans/${editId}`, payload); toast.success(`Plan “${payload.name}” updated successfully.`); }
      else { await api.post("/admin/plans", { ...payload, id: v.id.trim() }); toast.success(`Plan “${payload.name}” created. ${payload.active ? "It is now available to vendors." : "It is inactive until you activate it."}`); }
      setOpen(false); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleActive = useCallback(async (p) => {
    const next = !p.active;
    if (!next && p.subscribers > 0 && !window.confirm(`Deactivate “${p.name}”? ${p.subscribers} store(s) stay on it, but new vendors can no longer choose it.`)) return;
    try {
      await api.put(`/admin/plans/${p.id}`, { ...toPayload(p), active: next });
      toast.success(`“${p.name}” ${next ? "activated and visible to vendors" : "deactivated"}.`);
      load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  }, [load]);

  const del = useCallback(async (p) => {
    if (!window.confirm(`Delete the “${p.name}” plan? This cannot be undone.`)) return;
    try { await api.delete(`/admin/plans/${p.id}`); toast.success(`Plan “${p.name}” deleted.`); load(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  }, [load]);

  const columns = useMemo(() => [
    { key: "name", header: "Plan", cell: (p) => (
      <div className="min-w-[150px]">
        <div className="font-semibold text-neutral-900 flex items-center gap-1.5">{p.name}
          {p.popular && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold bg-orange-50 text-orange-600 border border-orange-200 px-1.5 rounded"><Star size={9} /> Popular</span>}
        </div>
        <div className="text-[11px] text-slate-500 font-mono">{p.id}</div>
      </div>
    ) },
    { key: "price", header: "Price", accessor: (p) => p.price, cell: (p) => <span className="font-semibold whitespace-nowrap">{p.price === 0 ? "Free" : <>{inr(p.price)}<span className="text-slate-400 font-normal">/mo</span></>}</span> },
    { key: "product_limit", header: "Products", accessor: (p) => p.product_limit, cell: (p) => <span className="text-slate-700">{limitText(p.product_limit)}</span> },
    { key: "includes", header: "Includes", hideBelow: "lg", sortable: false, cell: (p) => (
      <div className="flex flex-wrap gap-1">
        {p.ai_theme && <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-200"><Sparkles size={10} /> AI theme</span>}
        {p.custom_domain && <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-200"><Globe size={10} /> Domain</span>}
        {!p.ai_theme && !p.custom_domain && <span className="text-slate-400">—</span>}
      </div>
    ) },
    { key: "subscribers", header: "Stores", align: "center", accessor: (p) => p.subscribers },
    { key: "monthly_revenue", header: "Revenue/mo", align: "right", hideBelow: "md", accessor: (p) => p.monthly_revenue, cell: (p) => <span className="font-semibold text-green-600">{inr(p.monthly_revenue)}</span> },
    { key: "sort_order", header: "Order", align: "center", hideBelow: "lg", accessor: (p) => p.sort_order ?? 0 },
    { key: "active", header: "Status", accessor: (p) => (p.active ? "active" : "inactive"), cell: (p) => (
      <span className={`inline-block text-[11px] px-2 py-0.5 rounded-md border font-medium ${p.active ? "bg-green-50 text-green-700 border-green-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{p.active ? "Active" : "Inactive"}</span>
    ) },
    { key: "actions", header: "", sortable: false, align: "right", cell: (p) => (
      <div className="flex justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => openEdit(p)} data-testid={`plan-edit-${p.id}`} className={`${iconBtn} hover:bg-blue-50 hover:text-blue-600`} aria-label={`Edit ${p.name}`}><Pencil size={14} /></button>
        {p.id !== "free" && (
          <>
            <button onClick={() => toggleActive(p)} data-testid={`plan-toggle-${p.id}`} className={`${iconBtn} hover:bg-orange-50 hover:text-orange-600`} aria-label={p.active ? `Deactivate ${p.name}` : `Activate ${p.name}`} title={p.active ? "Deactivate" : "Activate"}><Power size={14} /></button>
            <button onClick={() => del(p)} data-testid={`plan-delete-${p.id}`} className={`${iconBtn} hover:bg-red-50 hover:text-red-600`} aria-label={`Delete ${p.name}`}><Trash2 size={14} /></button>
          </>
        )}
      </div>
    ) },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [toggleActive, del]);

  const filters = useMemo(() => [
    { key: "active", label: "All status", accessor: (p) => (p.active ? "active" : "inactive"), options: [{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }] },
    { key: "type", label: "Free & paid", accessor: (p) => (p.price === 0 ? "free" : "paid"), options: [{ value: "free", label: "Free" }, { value: "paid", label: "Paid" }] },
  ], []);

  const stats = [
    { label: "Total plans", value: plans.length, tone: "from-blue-50 to-white border-blue-100" },
    { label: "Active plans", value: plans.filter((p) => p.active).length, tone: "from-green-50 to-white border-green-100" },
    { label: "Paying stores", value: plans.filter((p) => p.price > 0).reduce((n, p) => n + p.subscribers, 0), tone: "from-orange-50 to-white border-orange-100" },
    { label: "Monthly revenue", value: inr(plans.reduce((n, p) => n + p.monthly_revenue, 0)), tone: "from-blue-50 to-white border-blue-100" },
  ];

  const previewFeatures = v.features.split("\n").map((l) => l.trim()).filter(Boolean);

  return (
    <div className="space-y-4" data-testid="admin-plans">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className={pageTitle}>Plans</h1>
          <p className={pageSub}>Create and manage the subscription plans vendors can choose from.</p>
        </div>
        <button onClick={openNew} data-testid="plan-new-btn" className={btnPrimary}><Plus size={15} /> New plan</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((s) => (
          <div key={s.label} className={`rounded-xl border bg-gradient-to-br ${s.tone} px-4 py-3`}>
            <div className="text-[12px] text-slate-500">{s.label}</div>
            <div className="text-[22px] font-bold text-neutral-900 leading-tight">{s.value}</div>
          </div>
        ))}
      </div>

      <DataTable testId="plans-table" exportName="plans" columns={columns} data={plans} loading={loading} rowTestId={(p) => `plan-row-${p.id}`}
        searchKeys={["name", "id", "description"]} searchPlaceholder="Search plans…" filters={filters} defaultSort={{ key: "sort_order", dir: "asc" }}
        onRowClick={openEdit} emptyIcon={Layers} emptyTitle="No plans yet" emptyText="Create your first plan to start selling subscriptions." />

      <Modal open={open} onClose={() => setOpen(false)} size="xl" testId="plan-modal" title={editId ? `Edit plan — ${v.name || editId}` : "New plan"} subtitle="Fields marked * are required."
        footer={<>
          <button onClick={() => setOpen(false)} className={btnOutline}>Cancel</button>
          <button onClick={save} disabled={saving} data-testid="plan-save" className={btnPrimary}>{saving ? "Saving…" : editId ? "Save changes" : "Create plan"}</button>
        </>}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-6 gap-y-4">
          <div className="lg:col-span-8 grid grid-cols-6 gap-x-3 gap-y-3 content-start">
            <Field className="col-span-6 sm:col-span-3" label="Plan name" required maxLength={40} t="plan-name" placeholder="e.g. Growth"
              value={v.name} onBlur={bind("name").onBlur} error={bind("name").error}
              onChange={(x) => { set("name", x); if (!editId && !idEdited) set("id", slugify(x)); }} />
            <Field className="col-span-6 sm:col-span-3" label="Plan ID" required disabled={!!editId} maxLength={30} t="plan-id" inputClassName="font-mono"
              hint={editId ? "The ID can't be changed after creation." : "Used internally and in URLs."}
              value={v.id} onBlur={bind("id").onBlur} error={bind("id").error} onChange={(x) => { setIdEdited(x !== ""); set("id", x.toLowerCase()); }} />
            <Field className="col-span-3 sm:col-span-2" label="Price" required type="number" min="0" step="1" prefix="₹" suffix="/mo" t="plan-price" disabled={isFree}
              hint={isFree ? "The Free plan is always ₹0." : undefined} {...bind("price")} />
            <div className="col-span-3 sm:col-span-2">
              <Field label="Product limit" required={!v.unlimited} type="number" min="1" step="1" t="plan-limit" disabled={v.unlimited}
                value={v.unlimited ? "" : v.product_limit} placeholder={v.unlimited ? "Unlimited" : ""} onBlur={bind("product_limit").onBlur}
                error={bind("product_limit").error} onChange={(x) => set("product_limit", x)} />
            </div>
            <div className="col-span-6 sm:col-span-2 flex items-end pb-1.5">
              <CheckField label="Unlimited products" checked={v.unlimited} onChange={(c) => set("unlimited", c)} t="plan-unlimited" />
            </div>
            <Field className="col-span-6 sm:col-span-4" label="Short description" maxLength={120} t="plan-description" placeholder="e.g. For new and growing stores" {...bind("description")} />
            <Field className="col-span-6 sm:col-span-2" label="Display order" type="number" min="0" step="1" t="plan-order" hint="Lower shows first." {...bind("sort_order")} />
            <Field className="col-span-6" label="Features" type="textarea" rows={5} t="plan-features" placeholder={"One feature per line\nUp to 500 products\nPriority support"}
              hint="One per line, up to 12. Shown on the vendor's Plan & Billing page." {...bind("features")} />
            <div className="col-span-6 grid sm:grid-cols-2 gap-x-4 gap-y-2.5 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
              <CheckField label="Includes AI Theme Generator" hint="Vendors on this plan can generate themes with AI." checked={v.ai_theme} onChange={(c) => set("ai_theme", c)} t="plan-ai" />
              <CheckField label="Includes custom domain" hint="Vendors on this plan can map their own domain." checked={v.custom_domain} onChange={(c) => set("custom_domain", c)} t="plan-domain" />
              <CheckField label="Mark as popular" hint="Highlights the plan to vendors." checked={v.popular} onChange={(c) => set("popular", c)} t="plan-popular" />
              <CheckField label="Active" hint={isFree ? "The Free plan must stay active." : "Inactive plans can't be chosen by vendors."} checked={isFree ? true : v.active} onChange={(c) => { if (!isFree) set("active", c); }} t="plan-active" />
            </div>
          </div>

          {/* live preview as vendors will see it */}
          <div className="lg:col-span-4">
            <div className="text-[12px] font-semibold text-slate-700 mb-1">Preview</div>
            <div className={`rounded-xl p-4 border shadow-sm ${v.popular ? "border-orange-200 bg-orange-50/40" : "border-slate-200 bg-white"}`}>
              {v.popular && <div className="text-[10px] font-bold text-orange-600 mb-1 flex items-center gap-1"><Star size={10} /> POPULAR</div>}
              <div className="font-display text-[15px] font-bold text-neutral-900">{v.name || "Plan name"}</div>
              {v.description && <div className="text-[12px] text-slate-500">{v.description}</div>}
              <div className="mt-2 flex items-end gap-1">
                <span className="text-2xl font-extrabold">{isBlank(v.price) || toNum(v.price) === 0 ? (isBlank(v.price) ? "₹—" : "Free") : inr(toNum(v.price))}</span>
                {toNum(v.price) > 0 && <span className="text-[12px] text-slate-500 mb-1">/mo</span>}
              </div>
              <ul className="mt-3 space-y-1.5 text-[13px] text-slate-600">
                <li className="flex gap-2"><Check size={14} className="text-green-600 mt-0.5 shrink-0" />{v.unlimited ? "Unlimited products" : `Up to ${isBlank(v.product_limit) ? "—" : v.product_limit} products`}</li>
                {v.ai_theme && <li className="flex gap-2"><Check size={14} className="text-green-600 mt-0.5 shrink-0" />AI Theme Generator</li>}
                {v.custom_domain && <li className="flex gap-2"><Check size={14} className="text-green-600 mt-0.5 shrink-0" />Custom domain</li>}
                {previewFeatures.slice(0, 12).map((f, i) => <li key={i} className="flex gap-2"><Check size={14} className="text-green-600 mt-0.5 shrink-0" />{f}</li>)}
              </ul>
              {!(isFree || v.active) && <div className="mt-3 text-[11px] text-slate-500 border-t border-slate-200 pt-2">Inactive — hidden from vendors.</div>}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
